import React, { useState, useEffect, useMemo, useRef } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, PieChart, Pie } from 'recharts';
import { Plus, Trash2, Save, X, Clock, Check, Copy, Calendar, RefreshCw, User, Award, School, BookOpen, Beaker, FileText, Brain, GraduationCap, Heart, Edit, Book, BarChart2, Camera } from 'lucide-react';
import { useUser } from '../contexts/UserContext';
import AppLayout from '../components/AppLayout';
import { Link, useLocation } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';
import { useTheme } from '../contexts/ThemeContext';
import { useFirebase } from '../contexts/FirebaseContext';
import { getColorScheme } from '../utils/colorSchemes';

// Maintaining the full original functionality with completely redesigned UI
const ProfilePage = () => {
  const location = useLocation();
  const section = new URLSearchParams(location.search).get('section');
  // Original state and functionality
  const { userData, updateUserData } = useUser();
  const { showToast } = useToast();
  const { user: firebaseUser } = useFirebase();
  
  // ADD THEME CONTEXT
  const { theme, accentColor } = useTheme();
  
  // Calculate colors based on selected accent color
  const colors = useMemo(() => {
    const currentAccentColor = accentColor || 'teal';
    return getColorScheme(currentAccentColor, theme);
  }, [accentColor, theme]);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [personalInfoEditing, setPersonalInfoEditing] = useState(false);
  const [educationEditing, setEducationEditing] = useState(false);
  const [officeHoursEditing, setOfficeHoursEditing] = useState(false);
  const [interestedSchoolsEditing, setInterestedSchoolsEditing] = useState(false);
  const [courseProgressEditing, setCourseProgressEditing] = useState(false);
  
const [personalInfoForm, setPersonalInfoForm] = useState({});  const [educationList, setEducationList] = useState([]);
  const [officeHours, setOfficeHours] = useState([
    { day: 'Monday', available: false, slots: [] },
    { day: 'Tuesday', available: false, slots: [] },
    { day: 'Wednesday', available: false, slots: [] },
    { day: 'Thursday', available: false, slots: [] },
    { day: 'Friday', available: false, slots: [] },
    { day: 'Saturday', available: false, slots: [] },
    { day: 'Sunday', available: false, slots: [] },
  ]);
  
  const [medicalSchoolsList, setMedicalSchoolsList] = useState([]);
  const [courseProgressData, setCourseProgressData] = useState([]);

  const [activeTab, setActiveTab] = useState('overview');
  const [profileImage, setProfileImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [activeIndex, setActiveIndex] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [quickSelectDay, setQuickSelectDay] = useState(null);
  const fileInputRef = useRef(null);

  // Available time slots (30 min increments from 8 AM to 8 PM)
  const availableTimeSlots = Array.from({ length: 24 }, (_, i) => {
    const hour = Math.floor(i / 2) + 8;
    const minute = (i % 2) * 30;
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour > 12 ? hour - 12 : (hour === 0 ? 12 : hour);
    return `${displayHour}:${minute === 0 ? '00' : minute} ${period}`;
  });

  const timeSlotTemplates = [
    { name: "Morning Hours", slots: ["8:00 AM", "8:30 AM", "9:00 AM", "9:30 AM", "10:00 AM", "10:30 AM", "11:00 AM", "11:30 AM"] },
    { name: "Afternoon Hours", slots: ["12:00 PM", "12:30 PM", "1:00 PM", "1:30 PM", "2:00 PM", "2:30 PM", "3:00 PM", "3:30 PM"] },
    { name: "Evening Hours", slots: ["4:00 PM", "4:30 PM", "5:00 PM", "5:30 PM", "6:00 PM", "6:30 PM", "7:00 PM", "7:30 PM"] },
    { name: "Full Day", slots: availableTimeSlots },
    { name: "Every Hour", slots: availableTimeSlots.filter(slot => slot.includes(":00")) },
    { name: "Clear All", slots: [] }
  ];

  // Weekday groups for quick selection
  const dayGroups = [
    { name: "All Days", days: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"] },
    { name: "Weekdays", days: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"] },
    { name: "Weekend", days: ["Saturday", "Sunday"] }
  ];

  // Initializing user data
  useEffect(() => {
    try {
      if (userData.isLoading) {
        return;
      }

      if (userData.isAuthenticated) {
        if (!educationEditing && userData.education && Array.isArray(userData.education)) {
          setEducationList(userData.education.map(item => ({ ...item })));
        } else if (!educationEditing) {
          setEducationList([{ school: '', degree: '', year: '' }]);
        }
        
        if (!personalInfoEditing) setPersonalInfoForm({
          name: userData.name || userData.email?.split('@')[0] || '',
          email: userData.email || '',
          bio: userData.bio || '',
          major: userData.major || '',
          role: userData.role || userData.practitionerType || 'dentist',
  year: userData.year || '',
          specialty: userData.specialty || '',
          state: userData.state || '',
          yearsOfExperience: userData.yearsOfExperience || '',
        });
        
        if (!officeHoursEditing && userData.officeHours && Array.isArray(userData.officeHours)) {
          setOfficeHours(userData.officeHours.map(day => ({ ...day, slots: [...(day.slots || [])] })));
        }
        
        if (!personalInfoEditing && userData.profileImage) {
          setProfileImage(userData.profileImage);
          setImagePreview(userData.profileImage);
        }
        
        if (!interestedSchoolsEditing && userData.medicalSchools && Array.isArray(userData.medicalSchools)) {
          setMedicalSchoolsList(userData.medicalSchools.map(item => ({ ...item })));
        }
        
        if (!courseProgressEditing && userData.courseProgress && Array.isArray(userData.courseProgress)) {
          setCourseProgressData(userData.courseProgress.map(item => ({ ...item })));
        }
      } else {
        if (!firebaseUser) {
          window.location.href = '/';
          return;
        }

        setError("User not found. Please log in.");
        showToast("User not found. Please log in.", "error");
        setTimeout(() => {
          window.location.href = '/';
        }, 3000);
      }
    } catch (err) {
      const errorMsg = "Error loading user data: " + err.message;
      setError(errorMsg);
      showToast(errorMsg, "error");
    } finally {
      setLoading(false);
    }
  }, [firebaseUser, userData, showToast, personalInfoEditing, educationEditing, officeHoursEditing, interestedSchoolsEditing, courseProgressEditing]);

  useEffect(() => {
    if (loading || !section) return;
    const target = document.getElementById(section);
    target?.scrollIntoView?.({ block: 'start' });
    if (section === 'office-hours') setOfficeHoursEditing(true);
  }, [loading, section]);

  // Utility functions (preserving original functionality)
  const isMentor = () => {
    const role = userData?.role || personalInfoForm.role || '';
    return role.toLowerCase() === 'mentor';
  };
  
  // Roles are chosen during registration; an email address grants no extra permissions.
  const canChangeRole = () => false;

  // Event handlers for image management
// REPLACE the existing handleImageChange function with:
const handleImageChange = (e) => {
  const file = e.target.files[0];
  if (file) {
    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      showToast('Please select a valid image file (JPG, PNG, GIF, or WebP)', 'error');
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      showToast('Image size must be less than 5MB', 'error');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        if (personalInfoEditing) {
          setImagePreview(reader.result);
          showToast('Picture selected. Save personal information to keep it.', 'info');
          return;
        }
        await updateUserData({ profileImage: reader.result });
        setProfileImage(file);
        setImagePreview(reader.result);
        showToast('Profile picture updated successfully!', 'success');
      } catch (error) { showToast('Profile picture could not be saved. Please try again.', 'error'); }
    };
    reader.readAsDataURL(file);
  }
};

// REPLACE the existing removeImage function with:
const removeImage = async () => {
  if (personalInfoEditing) { setProfileImage(null); setImagePreview(null); return; }
  try {
    await updateUserData({ profileImage: null });
    setProfileImage(null);
    setImagePreview(null);
    showToast('Profile picture removed', 'info');
  } catch (error) { showToast('Profile picture could not be removed. Please try again.', 'error'); }
};

// ADD THIS FUNCTION RIGHT HERE:
const handleAvatarClick = () => {
  fileInputRef.current?.click();
};

  // Personal info form handlers
  const handlePersonalInfoChange = (e) => {
    const { name, value } = e.target;
    setPersonalInfoForm({
      ...personalInfoForm,
      [name]: value
    });
  };

  // Education handlers
  const handleEducationChange = (index, field, value) => {
    const updatedEducation = [...educationList];
    updatedEducation[index] = {
      ...updatedEducation[index],
      [field]: value
    };
    setEducationList(updatedEducation);
  };

  // Format year range for display
  const formatYearRange = (edu) => {
    if (edu.startYear && edu.endYear) {
      return `${edu.startYear} - ${edu.endYear}`;
    } else if (edu.startYear) {
      return `${edu.startYear} - Present`;
    } else if (edu.year) {
      // Fallback for old format
      return edu.year;
    }
    return 'Year';
  };

  const addEducation = () => {
    setEducationList([...educationList, { school: '', degree: '', year: '' }]);
  };

  const removeEducation = (index) => {
    if (educationList.length > 1) {
      const updated = educationList.filter((_, i) => i !== index);
      setEducationList(updated);
    }
  };

  // Office hours handlers
  const toggleDayAvailability = (dayIndex) => {
    const updatedOfficeHours = officeHours.map(day => ({ ...day, slots: [...(day.slots || [])] }));
    updatedOfficeHours[dayIndex].available = !updatedOfficeHours[dayIndex].available;
    
    if (!updatedOfficeHours[dayIndex].available) {
      updatedOfficeHours[dayIndex].slots = [];
    }
    
    setOfficeHours(updatedOfficeHours);
  };

  const toggleTimeSlot = (dayIndex, timeSlot) => {
    const updatedOfficeHours = officeHours.map(day => ({ ...day, slots: [...(day.slots || [])] }));
    const day = updatedOfficeHours[dayIndex];
    
    if (day.slots.includes(timeSlot)) {
      day.slots = day.slots.filter(slot => slot !== timeSlot);
    } else {
      day.slots = [...day.slots, timeSlot].sort((a, b) => {
        const aTime = parseTimeString(a);
        const bTime = parseTimeString(b);
        return aTime - bTime;
      });
    }
    
    setOfficeHours(updatedOfficeHours);
  };

  const applyTemplateToDay = (dayIndex, template) => {
    const updatedOfficeHours = officeHours.map(day => ({ ...day, slots: [...(day.slots || [])] }));
    const day = updatedOfficeHours[dayIndex];
    
    if (!day.available) {
      day.available = true;
    }
    
    day.slots = [...template.slots];
    
    setOfficeHours(updatedOfficeHours);
  };

  const applyTemplateToDays = (dayNames, template) => {
    const updatedOfficeHours = officeHours.map(day => ({ ...day, slots: [...(day.slots || [])] }));
    
    dayNames.forEach(dayName => {
      const dayIndex = updatedOfficeHours.findIndex(day => day.day === dayName);
      if (dayIndex !== -1) {
        updatedOfficeHours[dayIndex].available = template.slots.length > 0;
        updatedOfficeHours[dayIndex].slots = [...template.slots];
      }
    });
    
    setOfficeHours(updatedOfficeHours);
    setSelectedTemplate(null);
  };

  const copyHoursFromDay = (sourceDayIndex, targetDayIndex) => {
    const updatedOfficeHours = officeHours.map(day => ({ ...day, slots: [...(day.slots || [])] }));
    const sourceDay = updatedOfficeHours[sourceDayIndex];
    const targetDay = updatedOfficeHours[targetDayIndex];
    
    targetDay.available = sourceDay.available;
    targetDay.slots = [...sourceDay.slots];
    
    setOfficeHours(updatedOfficeHours);
  };

  const parseTimeString = (timeStr) => {
    const [time, period] = timeStr.split(' ');
    let [hours, minutes] = time.split(':').map(Number);
    
    if (period === 'PM' && hours < 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;
    
    return hours * 60 + minutes;
  };

  // Section edit handlers
  const handleEditPersonalInfo = () => {
    setPersonalInfoEditing(true);
  };

  const handleCancelPersonalInfo = () => {
    setPersonalInfoEditing(false);
    setPersonalInfoForm({
      name: userData.name || '',
      email: userData.email || '',
      bio: userData.bio || '',
      major: userData.major || '',
      role: userData.role || '',
  year: userData.year || '',
      specialty: userData.specialty || '',
      state: userData.state || '',
      yearsOfExperience: userData.yearsOfExperience || '',
    });
    setImagePreview(userData.profileImage || null);
    setProfileImage(null);
  };

  const handleSavePersonalInfo = async () => {
    try {
      const updatedUserData = {
        ...personalInfoForm,
        role: canChangeRole() ? personalInfoForm.role : userData.role,
      };
      
      if (imagePreview) {
        updatedUserData.profileImage = imagePreview;
      } else {
        updatedUserData.profileImage = null;
      }
      
      await updateUserData(updatedUserData);
      setPersonalInfoEditing(false);
      showNotification("Personal information saved successfully!");
    } catch (err) {
      const errorMsg = "Error saving personal information: " + err.message;
      showNotification(errorMsg, "error");
    }
  };

  const handleEditEducation = () => {
    setEducationEditing(true);
  };

  const handleCancelEducation = () => {
    setEducationEditing(false);
    setEducationList((userData.education || [{ school: '', degree: '', year: '' }]).map(item => ({ ...item })));
  };

  const handleSaveEducation = async () => {
    try {
      const filteredEducation = educationList.filter(edu => edu.school || edu.degree || edu.year);
      await updateUserData({ education: filteredEducation });
      setEducationEditing(false);
      showNotification("Education information saved successfully!");
    } catch (err) {
      const errorMsg = "Error saving education information: " + err.message;
      showNotification(errorMsg, "error");
    }
  };

  const handleEditOfficeHours = () => {
    setOfficeHoursEditing(true);
  };

  const handleCancelOfficeHours = () => {
    setOfficeHoursEditing(false);
    setOfficeHours(userData.officeHours ? userData.officeHours.map(day => ({ ...day, slots: [...(day.slots || [])] })) : [
      { day: 'Monday', available: false, slots: [] },
      { day: 'Tuesday', available: false, slots: [] },
      { day: 'Wednesday', available: false, slots: [] },
      { day: 'Thursday', available: false, slots: [] },
      { day: 'Friday', available: false, slots: [] },
      { day: 'Saturday', available: false, slots: [] },
      { day: 'Sunday', available: false, slots: [] },
    ]);
  };

  const handleSaveOfficeHours = async () => {
    try {
      await updateUserData({ officeHours: officeHours });
      setOfficeHoursEditing(false);
      showNotification("Office hours saved successfully!");
    } catch (err) {
      const errorMsg = "Error saving office hours: " + err.message;
      showNotification(errorMsg, "error");
    }
  };

  // Medical Schools Handlers
  const handleEditMedicalSchools = () => {
    setInterestedSchoolsEditing(true);
  };

  const handleCancelMedicalSchools = () => {
    setInterestedSchoolsEditing(false);
    setMedicalSchoolsList((userData.medicalSchools || []).map(item => ({ ...item })));
  };

  const handleSaveMedicalSchools = async () => {
    try {
      const filteredSchools = medicalSchoolsList.filter(school => school.name);
      await updateUserData({ medicalSchools: filteredSchools });
      setInterestedSchoolsEditing(false);
      showNotification("Interested medical schools saved successfully!");
    } catch (err) {
      const errorMsg = "Error saving medical schools: " + err.message;
      showNotification(errorMsg, "error");
    }
  };

  const addMedicalSchool = () => {
    setMedicalSchoolsList([
      ...medicalSchoolsList, 
      { id: Date.now(), name: '', reason: '' }
    ]);
  };

  const removeMedicalSchool = (id) => {
    const updatedSchools = medicalSchoolsList.filter(school => school.id !== id);
    setMedicalSchoolsList(updatedSchools);
  };

  const handleSchoolChange = (id, field, value) => {
    const updatedSchools = medicalSchoolsList.map(school => 
      school.id === id ? { ...school, [field]: value } : school
    );
    setMedicalSchoolsList(updatedSchools);
  };

  // Course Progress Handlers
  const handleEditCourseProgress = () => {
    setCourseProgressEditing(true);
  };

  const handleCancelCourseProgress = () => {
    setCourseProgressEditing(false);
    setCourseProgressData((userData.courseProgress || []).map(item => ({ ...item })));
  };

  const handleSaveCourseProgress = async () => {
    try {
      const validatedData = courseProgressData.map(course => {
        let { completed, remaining } = course;
        completed = Number(completed);
        remaining = Number(remaining);
        
        if (completed + remaining !== 100) {
          remaining = 100 - completed;
        }
        
        return {
          ...course,
          completed,
          remaining
        };
      });
      
      await updateUserData({ courseProgress: validatedData });
      setCourseProgressData(validatedData);
      setCourseProgressEditing(false);
      showNotification("Course progress saved successfully!");
    } catch (err) {
      const errorMsg = "Error saving course progress: " + err.message;
      showNotification(errorMsg, "error");
    }
  };

  const addCourse = () => {
    setCourseProgressData([
      ...courseProgressData, 
      { id: Date.now(), name: '', completed: 0, remaining: 100 }
    ]);
  };

  const removeCourse = (id) => {
    const updatedCourses = courseProgressData.filter(course => course.id !== id);
    setCourseProgressData(updatedCourses);
  };

  const handleCourseChange = (id, field, value) => {
    const updatedCourses = courseProgressData.map(course => {
      if (course.id === id) {
        const updatedCourse = { ...course, [field]: value };
        
        if (field === 'completed') {
          const completed = Number(value) || 0;
          updatedCourse.remaining = 100 - completed;
        }
        
        return updatedCourse;
      }
      return course;
    });
    
    setCourseProgressData(updatedCourses);
  };

  const showNotification = (message, type = 'success') => {
    showToast(message, type);
  };

  const handleMouseEnter = (_, index) => {
    setActiveIndex(index);
  };

  const handleMouseLeave = () => {
    setActiveIndex(null);
  };

  // Completely reimagined UI components
  const EditableButton = ({ isEditing, onEdit, onSave, onCancel, label }) => {
    if (!isEditing) {
      return (
        <button
          onClick={onEdit}
          aria-label={`Edit ${label}`}
          className="bg-green-50 hover:bg-green-100 text-green-700 hover:text-green-800 p-2 rounded-full flex items-center justify-center transition-all duration-300 group"
        >
          <Edit className="h-4 w-4 group-hover:scale-110 transition-transform" />
        </button>
      );
    }
    
    return (
      <div className="flex space-x-2">
        <button
          onClick={onCancel}
          aria-label={`Cancel ${label}`}
          className="bg-red-50 hover:bg-red-100 text-red-600 hover:text-red-700 p-2 rounded-full flex items-center justify-center transition-all duration-300 group"
        >
          <X className="h-4 w-4 group-hover:scale-110 transition-transform" />
        </button>
        <button
          onClick={onSave}
          aria-label={`Save ${label}`}
          className="bg-green-50 hover:bg-green-100 text-green-700 hover:text-green-800 p-2 rounded-full flex items-center justify-center transition-all duration-300 group"
        >
          <Check className="h-4 w-4 group-hover:scale-110 transition-transform" />
        </button>
      </div>
    );
  };

  // Custom TimeSlot component
  const TimeSlot = ({ time, isSelected, onClick }) => {
    return (
      <button
        onClick={onClick}
        aria-pressed={isSelected}
        className={`px-2 py-1 text-xs rounded-full transition-all duration-200 
          ${isSelected 
            ? 'text-white hover:opacity-80 transform hover:scale-105' 
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:text-gray-800'}`}
        style={isSelected ? { backgroundColor: colors.primary } : {}}
      >
        {isSelected && <Check className="h-3 w-3 inline mr-1" />}
        {time}
      </button>
    );
  };

  // Calculate the overall course completion percentage
  const calculateOverallProgress = () => {
    if (courseProgressData.length === 0) return 0;
    
    const totalCompleted = courseProgressData.reduce((sum, course) => sum + Number(course.completed), 0);
    return Math.round(totalCompleted / courseProgressData.length);
  };

  // Get a skill level description based on completion percentage
  const getSkillLevel = (percentage) => {
    if (!courseProgressData.length) return 'Not recorded';
    if (percentage >= 90) return 'Mostly complete';
    if (percentage >= 75) return 'In progress';
    if (percentage >= 50) return 'In progress';
    return 'Getting started';
  };

  // Get classes for the circle skill indicator
  const getSkillCircleClasses = (percentage) => {
    if (percentage >= 90) return 'bg-emerald-700 text-white';
    if (percentage >= 75) return 'bg-teal-600 text-white';
    if (percentage >= 50) return 'bg-amber-500 text-white';
    return 'bg-gray-300 text-gray-700';
  };

  // Get dynamic background color for skill level
  const getSkillCircleStyles = (percentage) => {
    if (percentage >= 90) return { backgroundColor: colors.accent, color: 'white' };
    if (percentage >= 75) return { backgroundColor: colors.primary, color: 'white' };
    if (percentage >= 50) return { backgroundColor: '#F59E0B', color: 'white' };
    return { backgroundColor: '#D1D5DB', color: '#374151' };
  };

  // Get dynamic progress bar color
  const getProgressBarColor = (percentage) => {
    if (percentage >= 90) return colors.accent;
    if (percentage >= 75) return colors.primary;
    if (percentage >= 50) return '#F59E0B';
    return '#9CA3AF';
  };

  // Render the completely reimagined profile page content
  const renderProfileContent = () => {
    return (
      <div className="font-sans">
        {/* Header Banner */}
        <div className="relative mb-12 overflow-hidden">
          <div 
            className="absolute inset-0 opacity-10"
            style={{ 
              background: `linear-gradient(to right, ${colors.primary}, ${colors.accent})` 
            }}
          ></div>
          <div className="relative z-10 max-w-screen-xl mx-auto px-4 py-10 md:py-16">
            <div className="flex flex-col md:flex-row items-start md:items-center">
              <div className="relative mb-6 md:mb-0 md:mr-8">
  {/* Enhanced Clickable Avatar */}
  <div 
    className="relative cursor-pointer group"
    onClick={handleAvatarClick}
    title="Click to change profile picture"
  >
    {imagePreview || userData.profileImage ? (
      <div className="w-24 h-24 md:w-32 md:h-32 rounded-full border-4 border-white shadow-lg overflow-hidden bg-white transition-transform duration-200 group-hover:scale-105">
        <img 
          src={imagePreview || userData.profileImage} 
          alt="Profile" 
          className="w-full h-full object-cover"
        />
      </div>
    ) : (
      <div 
        className="w-24 h-24 md:w-32 md:h-32 rounded-full border-4 border-white shadow-lg flex items-center justify-center text-white text-3xl md:text-4xl font-bold transition-transform duration-200 group-hover:scale-105"
        style={{ 
          background: `linear-gradient(to bottom right, ${colors.primary}, ${colors.accent})` 
        }}
      >
        {userData.name ? userData.name.charAt(0).toUpperCase() : "M"}
      </div>
    )}
    
    {/* Camera Overlay */}
    <div className="absolute inset-0 rounded-full bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-200 flex items-center justify-center">
      <Camera className="h-6 w-6 md:h-8 md:w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
    </div>
    
    {/* Status Indicator */}
    <div className="absolute bottom-0 right-0 transform translate-x-1/4 bg-white rounded-full p-2 shadow-lg">
      <div className="w-6 h-6 rounded-full bg-green-500 border-2 border-white"></div>
    </div>
  </div>
  
  {/* Hidden File Input */}
  <input
    ref={fileInputRef}
    type="file"
    accept="image/*"
    onChange={handleImageChange}
    className="hidden"
  />
  
  {/* Remove Image Button (only show if there's an image) */}
  {(imagePreview || userData.profileImage) && (
    <button
      onClick={(e) => {
        e.stopPropagation();
        removeImage();
      }}
      className="absolute -top-2 -right-2 bg-red-100 hover:bg-red-200 text-red-600 rounded-full p-1.5 shadow-sm transition-colors duration-200"
      title="Remove profile picture"
    >
      <X className="h-3 w-3" />
    </button>
  )}
</div>
              
              <div className="flex-1">
                <h1 className="text-3xl md:text-4xl font-extrabold text-gray-800 mb-2">
                  Welcome back, {userData.name?.split(' ')[0] || 'User'}!
                </h1>
                <p className="text-lg text-gray-600 max-w-3xl">
                  {isMentor() 
                    ? "Your mentor dashboard helps you track your availability and connect with pre-med students."
                    : "Tell us a little about yourself. This is your space to introduce who you are and what you do."}
                </p>
              </div>
              
              <div className="mt-6 md:mt-0 flex flex-col md:items-end space-y-2">
                <div 
                  className="inline-flex items-center px-4 py-2 text-white font-medium rounded-lg shadow-md"
                  style={{ backgroundColor: colors.primary }}
                >
                  <span className="mr-1.5 bg-white h-2.5 w-2.5 rounded-full animate-pulse"></span>
                  {isMentor() ? "Mentor Account" : "Pre-Med Student"}
                </div>
                
                <div className="flex items-center space-x-2">
                  <div className="text-sm text-gray-500">Completion</div>
                  <div className="w-20 h-2.5 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full" 
                      style={{ 
                        backgroundColor: colors.primary,
                        width: `${calculateOverallProgress()}%` 
                      }}
                    ></div>
                  </div>
                  <div className="text-sm font-medium text-gray-700">{calculateOverallProgress()}%</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-screen-xl mx-auto px-4 pb-20">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Left Column */}
            <div className="md:col-span-1 space-y-8">
              {/* Personal Information Card */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                  <div className="flex items-center">
                    <User className="h-5 w-5 mr-2" style={{ color: colors.primary }} />
                    <h2 className="font-bold text-gray-800">Personal Details</h2>
                  </div>
                  <EditableButton 
                    isEditing={personalInfoEditing} 
                    label="personal details"
                    onEdit={handleEditPersonalInfo}
                    onSave={handleSavePersonalInfo}
                    onCancel={handleCancelPersonalInfo}
                  />
                </div>
                {!personalInfoEditing ? (
                  <div className="p-6">
                    <div className="space-y-4">
                      <div>
                        <label className="block text-xs text-gray-500 uppercase tracking-wider mb-1">Full Name</label>
                        <p className="text-gray-800 font-medium">{userData.name}</p>
                      </div>
                      
                      <div>
                        <label className="block text-xs text-gray-500 uppercase tracking-wider mb-1">Email</label>
                        <p className="text-gray-800 font-medium">{userData.email}</p>
                      </div>
                      
                      <div>
                        <label className="block text-xs text-gray-500 uppercase tracking-wider mb-1">Role</label>
                        <p className="text-gray-800 font-medium capitalize">{userData.role}</p>
                      </div>
                      
                      {!isMentor() && (
  <div className="grid grid-cols-2 gap-4">
    <div>
      <label className="block text-xs text-gray-500 uppercase tracking-wider mb-1">Major</label>
      <p className="text-gray-800 font-medium">{userData.major}</p>
    </div>
    
    <div>
      <label className="block text-xs text-gray-500 uppercase tracking-wider mb-1">Graduation Year</label>
      <p className="text-gray-800 font-medium">{userData.year}</p>
    </div>
  </div>
)}
                      
                      {isMentor() && (
                        <>
                          <div>
                            <label className="block text-xs text-gray-500 uppercase tracking-wider mb-1">Specialty</label>
                            <p className="text-gray-800 font-medium">{userData.specialty || "General"}</p>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-xs text-gray-500 uppercase tracking-wider mb-1">State</label>
                              <p className="text-gray-800 font-medium">{userData.state || "Not specified"}</p>
                            </div>
                            
                            <div>
                              <label className="block text-xs text-gray-500 uppercase tracking-wider mb-1">Experience</label>
                              <p className="text-gray-800 font-medium">{userData.yearsOfExperience || "0"} years</p>
                            </div>
                          </div>
                        </>
                      )}
                      
                      <div>
                        <label className="block text-xs text-gray-500 uppercase tracking-wider mb-1">Bio</label>
                        <p className="text-gray-800">{userData.bio}</p>
                      </div>
                      
                    </div>
                  </div>
                ) : (
                  <div className="p-6">
                    <div className="space-y-4">
                      {/* Profile image editing */}
                      <div className="mb-6">
                        <label className="block text-xs text-gray-500 uppercase tracking-wider mb-2">Profile Picture</label>
                        <div className="flex flex-col items-center space-y-3">
                          {imagePreview ? (
                            <div className="relative">
                              <div className="w-24 h-24 rounded-full overflow-hidden border border-gray-200 shadow-md">
                                <img src={imagePreview} alt="Profile Preview" className="w-full h-full object-cover" />
                              </div>
                              <button
                                type="button"
                                onClick={removeImage}
                                className="absolute -top-2 -right-2 bg-red-100 text-red-600 rounded-full p-1 shadow-sm hover:bg-red-200 transition-colors"
                                title="Remove Image"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          ) : (
                            <div 
                              className="w-24 h-24 rounded-full flex items-center justify-center text-white text-3xl font-bold shadow-md"
                              style={{ 
                                background: `linear-gradient(to bottom right, ${colors.primary}, ${colors.accent})` 
                              }}
                            >
                              {personalInfoForm.name ? personalInfoForm.name.charAt(0).toUpperCase() : "U"}
                            </div>
                          )}
                          
                          <div className="flex items-center space-x-3">
                            <label 
                              className="cursor-pointer px-4 py-2 text-white rounded-lg transition-colors text-sm"
                              style={{ backgroundColor: colors.primary }}
                              onMouseEnter={(e) => e.target.style.backgroundColor = colors.accent}
                              onMouseLeave={(e) => e.target.style.backgroundColor = colors.primary}
                            >
                              {imagePreview ? "Change Photo" : "Upload Photo"}
                              <input
                                type="file"
                                accept="image/*"
                                onChange={handleImageChange}
                                className="hidden"
                              />
                            </label>
                            {imagePreview && (
                              <button
                                type="button"
                                onClick={removeImage}
                                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label htmlFor="name" className="block text-xs text-gray-500 uppercase tracking-wider mb-1">Name</label>
                            <input 
                              id="name" 
                              name="name" 
                              value={personalInfoForm.name} 
                              onChange={handlePersonalInfoChange} 
                              placeholder="Your name" 
                              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                              style={{ 
                                '--tw-ring-color': colors.primary,
                                '--tw-ring-opacity': '0.5'
                              }}
                              onFocus={(e) => e.target.style.borderColor = colors.primary}
                              onBlur={(e) => e.target.style.borderColor = '#D1D5DB'}
                            />
                          </div>
                          
                          <div>
                            <label htmlFor="email" className="block text-xs text-gray-500 uppercase tracking-wider mb-1">Email</label>
                            <input 
                              id="email" 
                              name="email" 
                              readOnly
                              title="Sign-in email (read-only)"
                              type="email" 
                              value={personalInfoForm.email} 
                              onChange={handlePersonalInfoChange} 
                              placeholder="Your email" 
                              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                              onFocus={(e) => e.target.style.borderColor = colors.primary}
                              onBlur={(e) => e.target.style.borderColor = '#D1D5DB'}
                            />
                          </div>
                        </div>
                        
                        <div>
                          <label htmlFor="role" className="block text-xs text-gray-500 uppercase tracking-wider mb-1">Role</label>
                          {canChangeRole() ? (
                            <select
                              id="role"
                              name="role"
                              value={personalInfoForm.role}
                              onChange={handlePersonalInfoChange}
                              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                              onFocus={(e) => e.target.style.borderColor = colors.primary}
                              onBlur={(e) => e.target.style.borderColor = '#D1D5DB'}
                            >
                              <option value="">Select Role</option>
                              <option value="Mentor">Mentor</option>
                              <option value="Undergrad Student">Undergrad Student</option>
                              <option value="Professional Student">Professional Student</option>
                            </select>
                          ) : (
                            <div className="flex items-center">
                              <input 
                                id="role"
                                name="role"
                                value={userData.role || personalInfoForm.role}
                                className="w-full p-2 border border-gray-300 rounded-lg bg-gray-100"
                                disabled
                              />
                              <div className="ml-2 text-amber-600 text-xs">
                                Role cannot be changed
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* Conditional fields based on role */}
{isMentor() ? (
  // Mentor fields
  <>
    <div>
      <label htmlFor="specialty" className="block text-xs text-gray-500 uppercase tracking-wider mb-1">Specialty</label>
      <input 
        id="specialty" 
        name="specialty" 
        value={personalInfoForm.specialty || ''} 
        onChange={handlePersonalInfoChange} 
        placeholder="Your specialty" 
        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
        onFocus={(e) => e.target.style.borderColor = colors.primary}
        onBlur={(e) => e.target.style.borderColor = '#D1D5DB'}
      />
    </div>
    
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label htmlFor="state" className="block text-xs text-gray-500 uppercase tracking-wider mb-1">State</label>
        <input 
          id="state" 
          name="state" 
          value={personalInfoForm.state || ''} 
          onChange={handlePersonalInfoChange} 
          placeholder="Your state" 
          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
          onFocus={(e) => e.target.style.borderColor = colors.primary}
          onBlur={(e) => e.target.style.borderColor = '#D1D5DB'}
        />
      </div>
      
      <div>
        <label htmlFor="yearsOfExperience" className="block text-xs text-gray-500 uppercase tracking-wider mb-1">Years of Experience</label>
        <input 
          id="yearsOfExperience" 
          name="yearsOfExperience" 
          type="number"
          min="0"
          value={personalInfoForm.yearsOfExperience || ''} 
          onChange={handlePersonalInfoChange} 
          placeholder="Years of experience" 
          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
          onFocus={(e) => e.target.style.borderColor = colors.primary}
          onBlur={(e) => e.target.style.borderColor = '#D1D5DB'}
        />
      </div>
    </div>
  </>
) : (
  // Student fields
  <div className="grid grid-cols-2 gap-4">
    <div>
      <label htmlFor="major" className="block text-xs text-gray-500 uppercase tracking-wider mb-1">Major</label>
      <input 
        id="major" 
        name="major" 
        value={personalInfoForm.major} 
        onChange={handlePersonalInfoChange} 
        placeholder="Your major" 
        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
        onFocus={(e) => e.target.style.borderColor = colors.primary}
        onBlur={(e) => e.target.style.borderColor = '#D1D5DB'}
      />
    </div>
    
    <div>
      <label htmlFor="year" className="block text-xs text-gray-500 uppercase tracking-wider mb-1">Expected Graduation Year</label>
      <select 
        id="year" 
        name="year" 
        value={personalInfoForm.year} 
        onChange={handlePersonalInfoChange} 
        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
        onFocus={(e) => e.target.style.borderColor = colors.primary}
        onBlur={(e) => e.target.style.borderColor = '#D1D5DB'}
      >
        <option value="">Select Year</option>
        {Array.from({ length: 8 }, (_, i) => {
          const year = new Date().getFullYear() + i;
          return (
            <option key={year} value={year.toString()}>
              {year}
            </option>
          );
        })}
      </select>
    </div>
  </div>
)}
                        
                        <div>
                          <label htmlFor="bio" className="block text-xs text-gray-500 uppercase tracking-wider mb-1">Bio</label>
                          <textarea 
                            id="bio" 
                            name="bio" 
                            value={personalInfoForm.bio} 
                            onChange={handlePersonalInfoChange} 
                            placeholder="Tell us about yourself" 
                            rows={4} 
                            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                            onFocus={(e) => e.target.style.borderColor = colors.primary}
                            onBlur={(e) => e.target.style.borderColor = '#D1D5DB'}
                          />
                        </div>
                  
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Educational Background Card */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                  <div className="flex items-center">
                    <GraduationCap className="h-5 w-5 mr-2" style={{ color: colors.primary }} />
                    <h2 className="font-bold text-gray-800">Educational Background</h2>
                  </div>
                  <EditableButton 
                    isEditing={educationEditing} 
                    label="education"
                    onEdit={handleEditEducation}
                    onSave={handleSaveEducation}
                    onCancel={handleCancelEducation}
                  />
                </div>
                
                {!educationEditing ? (
                  <div className="p-6">
                    {(userData.education && userData.education.length > 0) ? (
                      <div className="space-y-4">
                        {userData.education.map((edu, index) => (
                          <div key={index} className="relative pl-8 pb-5">
                            {/* Timeline dot and line */}
                            <div 
                              className="absolute left-0 top-0 w-6 h-6 rounded-full border-2 flex items-center justify-center z-10"
                              style={{ 
                                backgroundColor: colors.lightBg,
                                borderColor: colors.primary
                              }}
                            >
                              <BookOpen className="h-3 w-3" style={{ color: colors.primary }} />
                            </div>
                            {index < userData.education.length - 1 && (
                              <div 
                                className="absolute left-3 top-6 bottom-0 w-0.5"
                                style={{ backgroundColor: colors.lightBg }}
                              ></div>
                            )}
                            
                            <div className="ml-2">
                              <h3 className="text-lg font-bold text-gray-800">{edu.school || "School Name"}</h3>
                              <p className="font-medium" style={{ color: colors.primary }}>{edu.degree || "Degree"}</p>
                              <p className="text-sm text-gray-500 mt-1">{formatYearRange(edu)}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-6">
                        <BookOpen className="h-10 w-10 mx-auto text-gray-300 mb-2" />
                        <p className="text-gray-500">No education information provided</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="p-6">
                    <div className="flex justify-end mb-4">
                      <button 
                        type="button" 
                        onClick={addEducation} 
                        className="inline-flex items-center px-3 py-2 text-sm rounded-lg text-white transition-colors"
                        style={{ backgroundColor: colors.primary }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = colors.accent}
                        onMouseLeave={(e) => e.target.style.backgroundColor = colors.primary}
                      >
                        <Plus className="h-4 w-4 mr-1" /> Add School
                      </button>
                    </div>
                    
                    <div className="space-y-4">
                      {educationList.map((edu, index) => (
                        <div key={index} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="flex justify-between items-center mb-3">
                            <h3 className="font-medium text-gray-700">School #{index + 1}</h3>
                            <button
                              type="button"
                              onClick={() => removeEducation(index)}
                              className="p-1 text-sm bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
                              disabled={educationList.length <= 1}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                          
                          <div className="space-y-3">
                            <div>
                              <label htmlFor={`school-${index}`} className="block text-xs text-gray-500 uppercase tracking-wider mb-1">School Name</label>
                              <input 
                                id={`school-${index}`}
                                value={edu.school || ''}
                                onChange={(e) => handleEducationChange(index, 'school', e.target.value)}
                                placeholder="School name"
                                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                                onFocus={(e) => e.target.style.borderColor = colors.primary}
                                onBlur={(e) => e.target.style.borderColor = '#D1D5DB'}
                              />
                            </div>
                            
                            <div>
                              <label htmlFor={`degree-${index}`} className="block text-xs text-gray-500 uppercase tracking-wider mb-1">Degree/Program</label>
                              <input
                                id={`degree-${index}`}
                                value={edu.degree || ''}
                                onChange={(e) => handleEducationChange(index, 'degree', e.target.value)}
                                placeholder="Degree or program"
                                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                                onFocus={(e) => e.target.style.borderColor = colors.primary}
                                onBlur={(e) => e.target.style.borderColor = '#D1D5DB'}
                              />
                            </div>

                            <div>
                              <label className="block text-xs text-gray-500 uppercase tracking-wider mb-1">
                                <Calendar className="h-3 w-3 inline mr-1" />
                                School Years Attended
                              </label>
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label htmlFor={`start-year-${index}`} className="block text-xs text-gray-400 mb-1">Start Year</label>
                                  <input
                                    id={`start-year-${index}`}
                                    type="month"
                                    value={edu.startYear || ''}
                                    onChange={(e) => handleEducationChange(index, 'startYear', e.target.value)}
                                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                                    onFocus={(e) => e.target.style.borderColor = colors.primary}
                                    onBlur={(e) => e.target.style.borderColor = '#D1D5DB'}
                                    min="1950-01"
                                    max="2050-12"
                                  />
                                </div>
                                <div>
                                  <label htmlFor={`end-year-${index}`} className="block text-xs text-gray-400 mb-1">End Year (or Expected)</label>
                                  <input
                                    id={`end-year-${index}`}
                                    type="month"
                                    value={edu.endYear || ''}
                                    onChange={(e) => handleEducationChange(index, 'endYear', e.target.value)}
                                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                                    onFocus={(e) => e.target.style.borderColor = colors.primary}
                                    onBlur={(e) => e.target.style.borderColor = '#D1D5DB'}
                                    min="1950-01"
                                    max="2050-12"
                                  />
                                </div>
                              </div>
                              <p className="text-xs text-gray-400 mt-1">Select the month and year you started and ended (or expect to end)</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Interested Medical Schools (only for students) */}
              {!isMentor() && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                    <div className="flex items-center">
                      <School className="h-5 w-5 mr-2" style={{ color: colors.primary }} />
                      <h2 className="font-bold text-gray-800">Target Medical Schools</h2>
                    </div>
                    <EditableButton 
                      isEditing={interestedSchoolsEditing} 
                      label="target schools"
                      onEdit={handleEditMedicalSchools}
                      onSave={handleSaveMedicalSchools}
                      onCancel={handleCancelMedicalSchools}
                    />
                  </div>
                  
                  {!interestedSchoolsEditing ? (
                    <div className="p-6">
                      {medicalSchoolsList.length > 0 ? (
                        <div className="space-y-4">
                          {medicalSchoolsList.map((school) => (
                            <div key={school.id} className="bg-gray-50 rounded-lg p-4">
                              <div className="flex items-start">
                                <div 
                                  className="flex-shrink-0 h-10 w-10 rounded-full flex items-center justify-center"
                                  style={{ backgroundColor: colors.lightBg }}
                                >
                                  <Award className="h-5 w-5" style={{ color: colors.primary }} />
                                </div>
                                <div className="ml-3">
                                  <h3 className="font-bold text-gray-800">{school.name || "Medical Institution"}</h3>
                                  <div className="mt-1">
                                    <span className="text-xs text-gray-500 uppercase tracking-wider">Reason for interest</span>
                                    <p className="text-gray-700 mt-0.5">{school.reason || "Career advancement"}</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-6">
                          <School className="h-10 w-10 mx-auto text-gray-300 mb-2" />
                          <p className="text-gray-500">No medical schools added</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="p-6">
                      <div className="flex justify-end mb-4">
                        <button 
                          type="button" 
                          onClick={addMedicalSchool} 
                          className="inline-flex items-center px-3 py-2 text-sm rounded-lg text-white transition-colors"
                          style={{ backgroundColor: colors.primary }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = colors.accent}
                          onMouseLeave={(e) => e.target.style.backgroundColor = colors.primary}
                        >
                          <Plus className="h-4 w-4 mr-1" /> Add School
                        </button>
                      </div>
                      
                      <div className="space-y-4">
                        {medicalSchoolsList.map((school) => (
                          <div key={school.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                            <div className="flex justify-between items-center mb-3">
                              <h3 className="font-medium text-gray-700">Medical School</h3>
                              <button
                                type="button"
                                onClick={() => removeMedicalSchool(school.id)}
                                className="p-1 text-sm bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
                                disabled={medicalSchoolsList.length <= 1}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                            
                            <div className="space-y-3">
                              <div>
                                <label htmlFor={`school-name-${school.id}`} className="block text-xs text-gray-500 uppercase tracking-wider mb-1">School Name</label>
                                <input 
                                  id={`school-name-${school.id}`}
                                  value={school.name || ''}
                                  onChange={(e) => handleSchoolChange(school.id, 'name', e.target.value)}
                                  placeholder="Medical school name"
                                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                                  onFocus={(e) => e.target.style.borderColor = colors.primary}
                                  onBlur={(e) => e.target.style.borderColor = '#D1D5DB'}
                                />
                              </div>
                              
                              <div>
                                <label htmlFor={`school-reason-${school.id}`} className="block text-xs text-gray-500 uppercase tracking-wider mb-1">Reason for Interest</label>
                                <input 
                                  id={`school-reason-${school.id}`}
                                  value={school.reason || ''}
                                  onChange={(e) => handleSchoolChange(school.id, 'reason', e.target.value)}
                                  placeholder="Why are you interested in this school?"
                                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                                  onFocus={(e) => e.target.style.borderColor = colors.primary}
                                  onBlur={(e) => e.target.style.borderColor = '#D1D5DB'}
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Right (Main) Column */}
            <div className="md:col-span-2 space-y-8">
              {/* Course Progress Section (students only) */}
              {!isMentor() && (
                <div id="courses" className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                    <div className="flex items-center">
                      <BarChart2 className="h-5 w-5 mr-2" style={{ color: colors.primary }} />
                      <h2 className="font-bold text-gray-800">Course Progress</h2>
                    </div>
                    <EditableButton 
                      isEditing={courseProgressEditing} 
                      label="courses"
                      onEdit={handleEditCourseProgress}
                      onSave={handleSaveCourseProgress}
                      onCancel={handleCancelCourseProgress}
                    />
                  </div>
                  
                  {!courseProgressEditing ? (
                    <div className="p-6">
                      {courseProgressData.length === 0 && <p className="text-gray-500 mb-4">No courses recorded. Add your own course entries with the edit button.</p>}
                      {/* Multi-part visualization */}
                      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8">
                        {/* Left: Circular Progress */}
                        <div className="lg:col-span-4 flex flex-col items-center justify-center">
                          <div className="relative w-48 h-48">
                            <svg className="w-full h-full" viewBox="0 0 100 100">
                              {/* Background circle */}
                              <circle 
                                cx="50" 
                                cy="50" 
                                r="45" 
                                fill="none" 
                                stroke="#EEF2FF" 
                                strokeWidth="10" 
                              />
                              
                              {/* Progress circle */}
                              <circle 
                                cx="50" 
                                cy="50" 
                                r="45" 
                                fill="none" 
                                stroke={colors.primary}
                                strokeWidth="10" 
                                strokeDasharray={`${calculateOverallProgress() * 2.83} 283`} 
                                strokeDashoffset="0" 
                                transform="rotate(-90, 50, 50)" 
                              />
                              
                              {/* Text in the middle */}
                              <text 
                                x="50" 
                                y="45" 
                                textAnchor="middle" 
                                fontSize="18" 
                                fontWeight="bold" 
                                fill={colors.primary}
                              >
                                {calculateOverallProgress()}%
                              </text>
                              <text 
                                x="50" 
                                y="65" 
                                textAnchor="middle" 
                                fontSize="10" 
                                fill="#6B7280"
                              >
                                OVERALL
                              </text>
                            </svg>
                          </div>
                          <div className="mt-4 text-center">
                            <p className="text-lg font-bold text-gray-800">
                              {getSkillLevel(calculateOverallProgress())} Level
                            </p>
                            <p className="text-sm text-gray-500">
                              {calculateOverallProgress() >= 80 
                                ? "You're doing great! Keep up the excellent work." 
                                : "Continue focusing on your courses to improve."}
                            </p>
                          </div>
                        </div>
                        
                        {/* Right: Bar Chart */}
                        <div className="lg:col-span-8">
                          <div className="h-72">
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart
                                data={courseProgressData}
                                margin={{ top: 10, right: 30, left: 0, bottom: 5 }}
                                barSize={36}
                              >
                                <CartesianGrid strokeDasharray="3 3" stroke="#EEF2FF" />
                                <XAxis 
                                  dataKey="name" 
                                  tick={{ fill: '#4B5563' }} 
                                  axisLine={{ stroke: '#E5E7EB' }}
                                />
                                <YAxis 
                                  tick={{ fill: '#4B5563' }} 
                                  axisLine={{ stroke: '#E5E7EB' }}
                                  domain={[0, 100]}
                                  tickFormatter={(tick) => `${tick}%`}
                                />
                                <Tooltip 
                                  cursor={{ fill: 'rgba(243, 244, 246, 0.6)' }}
                                  contentStyle={{
                                    backgroundColor: 'white',
                                    border: '1px solid #E5E7EB',
                                    borderRadius: '0.5rem',
                                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                                  }}
                                  formatter={(value) => [`${value}%`, 'Completed']}
                                />
                                <Bar 
                                  dataKey="completed" 
                                  name="Completed" 
                                  radius={[4, 4, 0, 0]}
                                  onMouseEnter={handleMouseEnter}
                                  onMouseLeave={handleMouseLeave}
                                >
                                  {courseProgressData.map((entry, index) => (
                                    <Cell 
                                      key={`cell-${index}`} 
                                      fill={activeIndex === index ? colors.accent : colors.primary}
                                    />
                                  ))}
                                </Bar>
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        </div>
                      </div>
                      
                      {/* Individual Course Cards */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {courseProgressData.map((course) => (
                          <div key={course.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex items-center">
                                <div 
                                  className="h-8 w-8 rounded-lg mr-3 flex items-center justify-center"
                                  style={getSkillCircleStyles(course.completed)}
                                >
                                  {course.name === 'Biology' && <Beaker className="h-4 w-4" />}
                                  {course.name === 'Chemistry' && <Beaker className="h-4 w-4" />}
                                  {course.name === 'Physics' && <FileText className="h-4 w-4" />}
                                  {course.name === 'Psychology' && <Brain className="h-4 w-4" />}
                                  {!['Biology', 'Chemistry', 'Physics', 'Psychology'].includes(course.name) && <Book className="h-4 w-4" />}
                                </div>
                                <h3 className="font-bold text-gray-800">{course.name}</h3>
                              </div>
                              <span 
                                className="px-2 py-1 text-xs font-medium rounded-full"
                                style={{
                                  backgroundColor: course.completed >= 90 ? '#D1FAE5' : 
                                                course.completed >= 75 ? colors.lightBg : 
                                                course.completed >= 50 ? '#FEF3C7' : '#F3F4F6',
                                  color: course.completed >= 90 ? '#065F46' : 
                                         course.completed >= 75 ? colors.primary : 
                                         course.completed >= 50 ? '#92400E' : '#374151'
                                }}
                              >
                                {course.completed}% Complete
                              </span>
                            </div>
                            
                            <div className="mt-2">
                              <div className="flex justify-between text-xs mb-1">
                                <span className="text-gray-500">Progress</span>
                                <span className="text-gray-700 font-medium">{course.completed}%</span>
                              </div>
                              <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div 
                                  className="h-full rounded-full"
                                  style={{ 
                                    backgroundColor: getProgressBarColor(course.completed),
                                    width: `${course.completed}%` 
                                  }}
                                ></div>
                              </div>
                            </div>
                            
                            <div className="mt-3 flex justify-between items-center text-sm">
                              <div className="text-gray-500">
                                <span className="font-medium">{course.remaining}%</span> remaining
                              </div>
                              <div className="font-medium" style={{ color: colors.primary }}>
                                {getSkillLevel(course.completed)}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="p-6">
                      <div className="flex justify-end mb-4">
                        <button 
                          type="button" 
                          onClick={addCourse} 
                          className="inline-flex items-center px-3 py-2 text-sm rounded-lg text-white transition-colors"
                          style={{ backgroundColor: colors.primary }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = colors.accent}
                          onMouseLeave={(e) => e.target.style.backgroundColor = colors.primary}
                        >
                          <Plus className="h-4 w-4 mr-1" /> Add Course
                        </button>
                      </div>
                      
                      <div className="space-y-4">
                        {courseProgressData.map((course) => (
                          <div key={course.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                            <div className="flex justify-between items-center mb-3">
                              <h3 className="font-medium text-gray-700">Course</h3>
                              <button
                                type="button"
                                onClick={() => removeCourse(course.id)}
                                className="p-1 text-sm bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
                                disabled={courseProgressData.length <= 1}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                            
                            <div className="space-y-3">
                              <div>
                                <label htmlFor={`course-name-${course.id}`} className="block text-xs text-gray-500 uppercase tracking-wider mb-1">Course Name</label>
                                <input 
                                  id={`course-name-${course.id}`}
                                  value={course.name || ''}
                                  onChange={(e) => handleCourseChange(course.id, 'name', e.target.value)}
                                  placeholder="Course name"
                                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                                  onFocus={(e) => e.target.style.borderColor = colors.primary}
                                  onBlur={(e) => e.target.style.borderColor = '#D1D5DB'}
                                />
                              </div>
                              
                              <div>
                                <label htmlFor={`course-completed-${course.id}`} className="block text-xs text-gray-500 uppercase tracking-wider mb-1">
                                  Completion Percentage (0-100)
                                </label>
                                <div className="flex items-center space-x-2">
                                  <input 
                                    id={`course-completed-${course.id}`}
                                    type="number"
                                    min="0"
                                    max="100"
                                    value={course.completed || 0}
                                    onChange={(e) => handleCourseChange(course.id, 'completed', e.target.value)}
                                    placeholder="Percentage completed"
                                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                                    onFocus={(e) => e.target.style.borderColor = colors.primary}
                                    onBlur={(e) => e.target.style.borderColor = '#D1D5DB'}
                                  />
                                  <span className="text-gray-700">% Complete</span>
                                </div>
                              </div>
                              
                              <div>
                                <label className="block text-xs text-gray-500 uppercase tracking-wider mb-1">Remaining</label>
                                <div className="flex items-center space-x-2">
                                  <input 
                                    type="number"
                                    value={course.remaining || 0}
                                    disabled
                                    className="w-full p-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-500"
                                  />
                                  <span className="text-gray-700">% Remaining</span>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                  This value is automatically calculated
                                </p>
                              </div>
                              
                              {/* Progress Bar Preview */}
                              <div className="mt-2">
                                <label className="block text-xs text-gray-500 uppercase tracking-wider mb-1">Preview</label>
                                <div className="w-full h-2.5 bg-gray-200 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full rounded-full"
                                    style={{ 
                                      backgroundColor: getProgressBarColor(course.completed),
                                      width: `${course.completed || 0}%` 
                                    }}
                                  ></div>
                                </div>
                                <div className="mt-1 flex justify-between text-xs text-gray-500">
                                  <span>0%</span>
                                  <span>{course.completed}%</span>
                                  <span>100%</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Office Hours Section (mentors only) */}
              {isMentor() && (
                <div id="office-hours" className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                    <div className="flex items-center">
                      <Calendar className="h-5 w-5 mr-2" style={{ color: colors.primary }} />
                      <h2 className="font-bold text-gray-800">Office Hours</h2>
                    </div>
                    <EditableButton 
                      isEditing={officeHoursEditing} 
                      label="office hours"
                      onEdit={handleEditOfficeHours}
                      onSave={handleSaveOfficeHours}
                      onCancel={handleCancelOfficeHours}
                    />
                  </div>
                  
                  {!officeHoursEditing ? (
                    <div className="p-6">
                      <div className="mb-4">
                        <h3 className="text-lg font-medium text-gray-800 mb-2">Your Weekly Availability</h3>
                        <p className="text-gray-600 mb-4">
                          Times when you're available for student mentoring sessions each week.
                        </p>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {officeHours.map((day) => (
                          <div 
                            key={day.day} 
                            className={`p-4 rounded-lg border ${
                              day.available 
                                ? 'bg-gray-50' 
                                : 'bg-gray-50'
                            }`}
                            style={day.available ? { 
                              borderColor: colors.lightBg, 
                              backgroundColor: colors.lightBg + '20' 
                            } : { borderColor: '#E5E7EB' }}
                          >
                            <div className="flex items-center justify-between mb-3">
                              <h4 
                                className="font-medium"
                                style={{ color: day.available ? colors.primary : '#6B7280' }}
                              >
                                {day.day}
                              </h4>
                              {day.available ? (
                                <span className="flex items-center text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full mr-1"></span>
                                  Available
                                </span>
                              ) : (
                                <span className="flex items-center text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mr-1"></span>
                                  Unavailable
                                </span>
                              )}
                            </div>
                            
                            {day.available && (
                              <>
                                {day.slots.length > 0 ? (
                                  <div className="flex flex-wrap gap-1 mt-2">
                                    {day.slots.map(slot => (
                                      <span 
                                        key={slot} 
                                        className="px-2 py-0.5 rounded-full text-xs font-medium"
                                        style={{ 
                                          backgroundColor: colors.lightBg, 
                                          color: colors.primary 
                                        }}
                                      >
                                        {slot}
                                      </span>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-sm" style={{ color: colors.primary }}>Available all day</p>
                                )}
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="p-6">
                      {/* Quick Setup Options */}
                      <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <h3 className="text-md font-medium text-gray-800 mb-3">Quick Setup Options</h3>
                        
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                          {/* Day Selection */}
                          <div>
                            <label className="block text-xs text-gray-500 uppercase tracking-wider mb-2">Apply To:</label>
                            <div className="grid grid-cols-2 gap-2">
                              {dayGroups.map(group => (
                                <button
                                  key={group.name}
                                  onClick={() => setQuickSelectDay(group)}
                                  className={`p-2 text-sm rounded-lg transition-colors ${
                                    quickSelectDay?.name === group.name 
                                      ? 'text-white' 
                                      : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                                  }`}
                                  style={quickSelectDay?.name === group.name ? 
                                    { backgroundColor: colors.primary } : 
                                    { borderColor: colors.lightBg }
                                  }
                                  onMouseEnter={(e) => {
                                    if (quickSelectDay?.name !== group.name) {
                                      e.target.style.borderColor = colors.primary;
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (quickSelectDay?.name !== group.name) {
                                      e.target.style.borderColor = colors.lightBg;
                                    }
                                  }}
                                >
                                  {group.name}
                                </button>
                              ))}
                              <button
                                onClick={() => setQuickSelectDay(null)}
                                className="p-2 text-sm rounded-lg bg-white border text-gray-700 hover:bg-gray-50"
                                style={{ borderColor: colors.lightBg }}
                                onMouseEnter={(e) => e.target.style.borderColor = colors.primary}
                                onMouseLeave={(e) => e.target.style.borderColor = colors.lightBg}
                              >
                                Individual Day
                              </button>
                            </div>
                          </div>
                          
                          {/* Time Templates */}
                          <div>
                            <label className="block text-xs text-gray-500 uppercase tracking-wider mb-2">Time Templates:</label>
                            <div className="grid grid-cols-2 gap-2">
                              {timeSlotTemplates.map(template => (
                                <button
                                  key={template.name}
                                  onClick={() => setSelectedTemplate(template)}
                                  className={`p-2 text-sm rounded-lg transition-colors ${
                                    selectedTemplate?.name === template.name 
                                      ? 'text-white' 
                                      : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                                  }`}
                                  style={selectedTemplate?.name === template.name ? 
                                    { backgroundColor: colors.primary } : 
                                    { borderColor: colors.lightBg }
                                  }
                                  onMouseEnter={(e) => {
                                    if (selectedTemplate?.name !== template.name) {
                                      e.target.style.borderColor = colors.primary;
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (selectedTemplate?.name !== template.name) {
                                      e.target.style.borderColor = colors.lightBg;
                                    }
                                  }}
                                >
                                  {template.name}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                        
                        {/* Apply Template Button */}
                        {quickSelectDay && selectedTemplate && (
                          <div className="mt-4 flex justify-end">
                            <button
                              onClick={() => applyTemplateToDays(quickSelectDay.days, selectedTemplate)}
                              className="inline-flex items-center px-4 py-2 text-white rounded-lg transition-colors"
                              style={{ backgroundColor: colors.primary }}
                              onMouseEnter={(e) => e.target.style.backgroundColor = colors.accent}
                              onMouseLeave={(e) => e.target.style.backgroundColor = colors.primary}
                            >
                              <RefreshCw className="mr-2 h-4 w-4" />
                              Apply {selectedTemplate.name} to {quickSelectDay.name}
                            </button>
                          </div>
                        )}
                      </div>
                      
                      {/* Individual Days */}
                      <div className="space-y-6">
                        {officeHours.map((day, dayIndex) => (
                          <div key={day.day} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                            <div className="flex items-center justify-between mb-4">
                              <div className="flex items-center">
                                <input
                                  type="checkbox"
                                  id={`available-${day.day}`}
                                  checked={day.available}
                                  aria-label={`${day.day} available`}
                                  onChange={() => toggleDayAvailability(dayIndex)}
                                  className="h-5 w-5 rounded border-gray-300"
                                  style={{ 
                                    accentColor: colors.primary,
                                    '--tw-ring-color': colors.primary 
                                  }}
                                />
                                <label htmlFor={`available-${day.day}`} className="ml-2 font-medium text-gray-700">
                                  {day.day}
                                </label>
                              </div>
                              
                              <div className="flex space-x-2">
                                {selectedTemplate && (
                                  <button
                                    onClick={() => applyTemplateToDay(dayIndex, selectedTemplate)}
                                    className="inline-flex items-center px-3 py-1 text-xs text-white rounded-lg transition-colors"
                                    style={{ backgroundColor: colors.primary }}
                                    onMouseEnter={(e) => e.target.style.backgroundColor = colors.accent}
                                    onMouseLeave={(e) => e.target.style.backgroundColor = colors.primary}
                                    disabled={!day.available}
                                  >
                                    <RefreshCw className="mr-1 h-3 w-3" />
                                    Apply {selectedTemplate.name}
                                  </button>
                                )}
                                
                                {dayIndex > 0 && (
                                  <button
                                    onClick={() => copyHoursFromDay(dayIndex - 1, dayIndex)}
                                    className="inline-flex items-center px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                                    title={`Copy from ${officeHours[dayIndex - 1].day}`}
                                  >
                                    <Copy className="mr-1 h-3 w-3" />
                                    Copy from {officeHours[dayIndex - 1].day}
                                  </button>
                                )}
                              </div>
                            </div>
                            
                            {day.available && (
                              <div>
                                <p className="text-sm text-gray-600 mb-3">Select available time slots:</p>
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                                  {availableTimeSlots.map(timeSlot => (
                                    <TimeSlot
                                      key={timeSlot}
                                      time={timeSlot}
                                      isSelected={day.slots.includes(timeSlot)}
                                      onClick={() => toggleTimeSlot(dayIndex, timeSlot)}
                                    />
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Main component return - preserving original AppLayout
  return (
    <AppLayout activeTab="settings">
      {isMentor() && !firebaseUser?.isAnonymous && <div className="mb-5 p-4 bg-white rounded-lg text-[#6B2B2B]"><Link className="underline font-medium" to="/app/mentor/listing">Manage my directory listing</Link><p className="text-sm mt-1">Directory publication is separate from your private profile and requires your explicit consent.</p></div>}
      {loading || userData.isLoading ? (
        <div className="flex justify-center items-center h-80">
          <div className="text-center">
            <div 
              className="w-16 h-16 border-4 border-t-4 rounded-full animate-spin mx-auto mb-4"
              style={{ 
                borderColor: colors.lightBg,
                borderTopColor: colors.primary 
              }}
            ></div>
            <p className="text-gray-600 text-lg">Loading your profile...</p>
          </div>
        </div>
      ) : error ? (
        <div className="flex justify-center items-center h-80">
          <div className="text-center p-8 max-w-md">
            <div className="bg-red-100 text-red-600 rounded-full p-3 w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <X className="h-8 w-8" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">Error Loading Profile</h3>
            <p className="text-red-600">{error}</p>
          </div>
        </div>
      ) : !userData.isAuthenticated ? (
        <div className="flex justify-center items-center h-80">
          <div className="text-center p-8 max-w-md">
            <div className="bg-amber-100 text-amber-600 rounded-full p-3 w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <User className="h-8 w-8" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">Not Logged In</h3>
            <p className="text-gray-600">No user data found. Redirecting to login...</p>
          </div>
        </div>
      ) : (
        renderProfileContent()
      )}
    </AppLayout>
  );
};

export default function ProfileScreen() {
  const { user } = useFirebase();
  return user ? <ProfilePage key={user.uid} /> : null;
}
